import { IResourceComponentsProps, useLogin, useRouterContext } from "@pankod/refine-core";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Helmet } from "react-helmet-async";

// Validation schema
const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  password: z
    .string()
    .min(1, "Password is required")
    .min(6, "Password must be at least 6 characters"),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const Login: React.FC<IResourceComponentsProps> = () => {
  const { Link } = useRouterContext();
  const { mutate: login } = useLogin();
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: "onChange", // Show errors as user types
    reValidateMode: "onChange",
  });

  const onSubmit = (data: LoginFormData) => {
    login({ email: data.email, password: data.password });
  };

  return (
    <>
      {/* Background gradient */}
      <div style={{ position: "relative" }}>
        <img
          src="/images/gradients/blue-blob.png"
          alt=""
          style={{
            pointerEvents: "none",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) scale(100%)",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            zIndex: 0,
          }}
        />

        {/* Main container - grid h-full max-w-md mx-auto my-auto text-center place-items-center pt-16 sm:pt-0 */}
        <div
          className="login-container"
          style={{
            display: "grid",
            height: "100vh",
            maxWidth: "28rem",
            margin: "auto",
            textAlign: "center",
            placeItems: "center",
            paddingTop: "4rem",
            paddingLeft: "1.5rem",
            paddingRight: "1.5rem",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div>
            {/* Logo - flex flex-row justify-center gap-2 mt-2 mb-6 align-middle */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                gap: "0.5rem",
                marginTop: "0.5rem",
                marginBottom: "1.5rem",
                alignItems: "middle",
              }}
            >
              <img
                src="/images/logo.png?v=2"
                alt="logo"
                style={{ objectFit: "contain", width: "5rem" }}
              />
            </div>

            {/* Form container - flex flex-col gap-3 mx-auto mt-6 */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
                margin: "0 auto",
                marginTop: "1.5rem",
              }}
            >
              {/* Heading - mx-auto mb-6 text-4xl font-bold */}
              <h1
                style={{
                  margin: "0 auto 1.5rem",
                  fontSize: "2.25rem",
                  fontWeight: "bold",
                }}
              >
                Login to your account
              </h1>

              <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ position: "relative" }}>
                {/* Loading overlay for form content */}
                {isSubmitting && (
                  <div
                    className="login-form-loading-overlay"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      borderRadius: "0.5rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 10,
                      backdropFilter: "blur(3px)",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}>
                      <div
                        style={{
                          width: "3rem",
                          height: "3rem",
                          border: "3px solid #e4e4e7",
                          borderTop: "3px solid #09090b",
                          borderRadius: "50%",
                          animation: "spin 0.8s linear infinite",
                        }}
                        role="status"
                        aria-label="Loading"
                      />
                      <p style={{ fontSize: "0.875rem", color: "#71717a", fontWeight: "500", margin: 0 }}>
                        Logging in...
                      </p>
                    </div>
                  </div>
                )}
                <style>{`
                  @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                  }
                `}</style>
                {/* Email input - mt-4 mb-6 */}
                <div style={{ marginTop: "1rem", marginBottom: errors.email ? "0.5rem" : "1.5rem" }}>
                  <input
                    {...register("email")}
                    type="email"
                    placeholder="Email"
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.75rem",
                      marginBottom: "0.25rem",
                      fontSize: "1rem",
                      backgroundColor: "white",
                      border: errors.email ? "2px solid #ef4444" : "1px solid #374151",
                      borderRadius: "0.375rem",
                      outline: "none",
                      transition: "border-color 0.2s",
                    }}
                    className="placeholder-slate-400"
                    aria-invalid={errors.email ? "true" : "false"}
                  />
                  {errors.email && (
                    <p
                      style={{
                        fontSize: "0.8125rem",
                        color: "#ef4444",
                        marginTop: "0.375rem",
                        marginBottom: "0",
                        fontWeight: "600",
                        textAlign: "left",
                        display: "block",
                        lineHeight: "1.4",
                        minHeight: "1.25rem",
                      }}
                      role="alert"
                    >
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Password input */}
                <div style={{ marginBottom: errors.password ? "0.5rem" : "0" }}>
                  <input
                    {...register("password")}
                    type="password"
                    placeholder="Password"
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "0.75rem",
                      marginBottom: "0.25rem",
                      fontSize: "1rem",
                      backgroundColor: "white",
                      border: errors.password ? "2px solid #ef4444" : "1px solid #374151",
                      borderRadius: "0.375rem",
                      outline: "none",
                      transition: "border-color 0.2s",
                    }}
                    className="placeholder-slate-400"
                    aria-invalid={errors.password ? "true" : "false"}
                  />
                  {errors.password && (
                    <p
                      style={{
                        fontSize: "0.8125rem",
                        color: "#ef4444",
                        marginTop: "0.375rem",
                        marginBottom: "0",
                        fontWeight: "600",
                        textAlign: "left",
                        display: "block",
                        lineHeight: "1.4",
                        minHeight: "1.25rem",
                      }}
                      role="alert"
                    >
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Forgot password link - text-end pt-3 pb-1 */}
                <p style={{ textAlign: "end", paddingTop: "0.75rem", paddingBottom: "0.25rem" }}>
                  <Link
                    to="/forgot-password"
                    style={{
                      textDecoration: "underline",
                      color: "inherit",
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      (e.target as HTMLElement).style.color = "#3f8ef1";
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      (e.target as HTMLElement).style.color = "inherit";
                    }}
                  >
                    Forgot password?
                  </Link>
                </p>

                {/* Login button - w-full h-12 mt-3 text-xl rounded-full */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    transition: "all 0.3s",
                    padding: "0.25rem 0.75rem",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    textAlign: "center",
                    backgroundColor: isSubmitting ? "#71717a" : "#000000",
                    color: "white",
                    width: "100%",
                    height: "3rem",
                    marginTop: "0.75rem",
                    fontSize: "1.25rem",
                    borderRadius: "9999px",
                    border: "none",
                    opacity: isSubmitting ? 0.7 : 1,
                  }}
                  className="group"
                >
                  <div style={{ overflow: "hidden", lineHeight: "1" }}>
                    <div
                      style={{
                        transform: "translateY(0)",
                        transition: "transform 0.5s",
                      }}
                      className="group-hover:-translate-y-full"
                    >
                      <div style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem", width: "100%" }}>
                        {isSubmitting ? "Logging in..." : "Login"}
                      </div>
                      <div
                        style={{
                          position: "absolute",
                          paddingTop: "0.25rem",
                          paddingBottom: "0.25rem",
                          width: "100%",
                        }}
                      >
                        Login
                      </div>
                    </div>
                  </div>
                </button>

                {/* Register link - py-2 */}
                <p style={{ paddingTop: "0.5rem", paddingBottom: "0.5rem" }}>
                  Don't have an account yet?{" "}
                  <Link
                    to="/register"
                    style={{
                      textDecoration: "underline",
                      color: "inherit",
                    }}
                    onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      (e.target as HTMLElement).style.color = "#3f8ef1";
                    }}
                    onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => {
                      (e.target as HTMLElement).style.color = "inherit";
                    }}
                  >
                    Register
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
